import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IconButton, Autocomplete, TextField } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
  MobileShiftReport,
  MobileSalesDetails,
  MobilePosSummary,
  MobileProductList,
  MobileShiftSummary
} from '../../../SelfOrderComponents/Components/Reports';
import '../../../SelfOrderComponents/Components/Reports/mobileReports.css';
import './PortableScreen.css';
import { logOutUser } from '../../../Js/generalFunctions';
import { viewAllBranchesAPICall } from '../../../API/Settings/Company Settings/companySettingsAPI';

const PortableScreen = () => {
  const navigate = useNavigate();
  const [openReport, setOpenReport] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const companyProfileData = useSelector((state) => state.companyProfileSlice.value);
  const allBranchesList = useSelector((state) => state.allBranchesSlice.value);

  // Fetch branches on mount
  useEffect(() => {
    viewAllBranchesAPICall();
  }, []);

  // Auto-select first branch or user's branch
  useEffect(() => {
    if (allBranchesList?.length > 0 && !selectedBranch) {
      // Try to find user's branch first
      const userBranch = allBranchesList.find(b => b._id === userInfo?.branchPk);
      setSelectedBranch(userBranch || allBranchesList[0]);
    }
  }, [allBranchesList, userInfo?.branchPk]);

  const handleOpenReport = (reportType) => {
    setOpenReport(reportType);
  };

  const handleCloseReport = () => {
    setOpenReport(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("portableMode");
    // logOutUser is a curried function: logOutUser(isBtnClick, userRole, shiftStatus, confirmPasswordDialog)()
    // For admin logout without shift check, pass false for shiftStatus
    logOutUser(true, 'admin', false, null)();
  };

  return (
    <div className="portable-screen">
      {/* Header */}
      <div className="portable-header">
        <div className="portable-header-info">
          {companyProfileData?.logo && (
            <img src={companyProfileData.logo} alt="Logo" className="portable-logo" />
          )}
          <div>
            <h2>{companyProfileData?.companyName || 'Restaurant'}</h2>
            <p>{userInfo?.branchName || 'Branch'}</p>
          </div>
        </div>
        <IconButton onClick={handleLogout} className="portable-logout-btn">
          <LogoutIcon />
        </IconButton>
      </div>

      {/* User Info & Branch Selector */}
      <div className="portable-user-info">
        <span>Welcome, {userInfo?.userName || 'Admin'}</span>
        <Autocomplete
          size="small"
          options={allBranchesList || []}
          getOptionLabel={(option) => option?.branchName || ''}
          value={selectedBranch}
          onChange={(e, newValue) => setSelectedBranch(newValue)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Branch" 
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '8px',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.8)',
                },
                minWidth: 200
              }}
            />
          )}
        />
      </div>

      {/* Report Icons */}
      <div className="portable-content">
        <h3 className="portable-title">PORTABLE REPORTS</h3>
        <div className="report-icons-row portable-icons">
          <button className="report-icon-btn advance" onClick={() => handleOpenReport('advanceShift')}>
            <TrendingUpIcon />
            <span className="icon-label">Advance Shift</span>
          </button>
          <button className="report-icon-btn sales" onClick={() => handleOpenReport('salesDetails')}>
            <ReceiptLongIcon />
            <span className="icon-label">Sales Details</span>
          </button>
          <button className="report-icon-btn shift" onClick={() => handleOpenReport('shiftReport')}>
            <AssessmentIcon />
            <span className="icon-label">Shift Report</span>
          </button>
          <button className="report-icon-btn summary" onClick={() => handleOpenReport('posSummary')}>
            <SummarizeIcon />
            <span className="icon-label">POS Summary</span>
          </button>
          <button className="report-icon-btn products" onClick={() => handleOpenReport('productList')}>
            <InventoryIcon />
            <span className="icon-label">Products</span>
          </button>
        </div>
      </div>

      {/* Report Dialogs */}
      <MobileShiftReport 
        open={openReport === 'advanceShift'} 
        onClose={handleCloseReport}
        branchId={selectedBranch?._id}
        branchName={selectedBranch?.branchName}
      />
      <MobileSalesDetails 
        open={openReport === 'salesDetails'} 
        onClose={handleCloseReport}
        branchId={selectedBranch?._id}
        branchName={selectedBranch?.branchName}
      />
      <MobileShiftSummary 
        open={openReport === 'shiftReport'} 
        onClose={handleCloseReport}
        branchId={selectedBranch?._id}
        branchName={selectedBranch?.branchName}
      />
      <MobilePosSummary 
        open={openReport === 'posSummary'} 
        onClose={handleCloseReport}
        branchId={selectedBranch?._id}
        branchName={selectedBranch?.branchName}
      />
      <MobileProductList 
        open={openReport === 'productList'} 
        onClose={handleCloseReport}
        branchId={selectedBranch?._id}
        branchName={selectedBranch?.branchName}
      />
    </div>
  );
};

export default PortableScreen;
